import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import {
	AD_LAYOUT_STUBS,
	INTERLINK_SPECIAL_CASE,
	PRODUCTION_ORIGIN,
	PAGES,
} from "@consts";
import { getDateDataFromDateCode, isEmpty } from "@utils";

import wordleMixin from "@/plugins/mixins/wordle";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	name: "WordleHints",
	mixins: [wordleMixin],
	async fetch() {
		this.isLoading = true;
		if (process.client) {
			this.loadData().then(() => {
				this.isLoading = false;
			});
		} else if (process.server) {
			this.serverDateParam = this.getDateParam();
			await this.loadData();
		}
	},
	mounted() {
		if (this.serverDateParam) {
			console.warn(
				"WORDLE DEBUG >>> found a serverDateParam value",
				this.serverDateParam
			);
			const clientDateParam = this.getDateParam();
			console.warn(
				"WORDLE DEBUG >>> creating client time",
				clientDateParam
			);
			if (clientDateParam !== this.serverDateParam) {
				console.warn(
					"WORDLE DEBUG >>> client time and server time mismatch",
					{ client: clientDateParam, server: this.serverDateParam }
				);
				this.isLoading = true;
				this.loadData().then(() => {
					this.isLoading = false;
				});
			} else {
				this.isLoading = false;
			}
		}
	},
	head({ $buildMetaDataObject }) {
		const dateCode = this.getDateParam(this.isYesterdayPage).replace(
			/^\d{2}(\d{2})-(\d{2})-(\d{2})$/,
			"$1$2$3"
		);
		const date = getDateDataFromDateCode(dateCode);
		let title = this.$t("WordleHintsMetaTitle", {
			MonthDay: `${date.month} ${parseInt(date.day)}`,
		});
		let description = this.$t("WordleHintsMetaDescription");

		if (this.isYesterdayPage) {
			title = `3 Hints for Yesterday’s Wordle & The Answer (${
				date.month
			} ${parseInt(date.day)})`;
			description =
				"Up your game with helpful hints and answers for yesterday’s Wordle.";
		}

		const metaData = $buildMetaDataObject({
			title,
			description,
		});

		metaData.script = metaData.script || [];

		const dateParam = this.getDateParam(this.isYesterdayPage);
		let dateModified = dayjs.tz(dateParam, "UTC");
		if (this.isYesterdayPage) {
			dateModified = dateModified.add(1, "day");
		}

		metaData.script.push({
			hid: "jsonld-wordle-hints",
			type: "application/ld+json",
			json: {
				"@context": "https://schema.org",
				"@type": "NewsArticle",
				headline: title,
				image: [
					`${PRODUCTION_ORIGIN}/static/images/wordle-hints-og.jpeg`,
				],

				datePublished: "2022-05-26T00:00:00.000Z",
				dateModified: dateModified.toISOString(),
				author: [
					{
						"@type": "Person",
						name: "Kate Miller-Wilson",
						url: `${PRODUCTION_ORIGIN}/about/`,
					},
				],
			},
		});

		return metaData;
	},
	asyncData({ res }) {
		if (res) {
			const expiryDate = dayjs()
				.utc()
				.hour(0)
				.minute(0)
				.startOf("minute")
				.add(1, "day");

			res.setHeader(
				"Expires",
				expiryDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]")
			);
		}
	},
	data() {
		return {
			serverDateParam: null,
			AD_LAYOUT_STUBS,
			apiData: null,
			pastData: [],
			hints: [],
			isLoading: true,
		};
	},
	computed: {
		isYesterdayPage() {
			return this.$route.name === PAGES.WORDLE_YESTERDAY_HINTS;
		},
		h1Text() {
			const firstPath = this.isYesterdayPage ? "Yesterday's" : "Today's";

			return `${firstPath}&nbsp;Wordle Hints&nbsp;&&nbsp;Answer`;
		},
		subtitle() {
			if (this.isYesterdayPage) {
				return "Stumped on yesterday’s Wordle? We’re here to help with hints and answers!";
			}

			return "Stumped on Today’s Wordle? WordFinder has you covered with three hints to help you solve the puzzle.";
		},
		subHeading() {
			if (!this.apiData) {
				return null;
			}

			const index = this.apiData?.index || "";

			return `${
				this.isYesterdayPage ? "Yesterday’s" : "Today’s"
			}&nbsp;Wordle&nbsp;Hints&nbsp;&&nbsp;Clues (#${index})&nbsp;- ${
				this.currentDateString
			}`;
		},
		currentDateString() {
			if (!this.apiData) {
				return null;
			}

			const date = getDateDataFromDateCode(
				this.apiData.date,
				this.isSmallDevice
			);

			return `${date.month}&nbsp;${date.day},&nbsp;${date.year}`;
		},
		interlinkSpecialCase() {
			return INTERLINK_SPECIAL_CASE.LENGTH_5;
		},
		last15Answers() {
			if (isEmpty(this.pastData)) {
				return [];
			}

			// Concat the last 2 months of answers,to have at least 15 answers to slice from
			const concatArray = this.pastData[0].answers.concat(
				this.pastData[1].answers
			);

			return concatArray.slice(0, 15).map((item) => ({
				...item,
				date: getDateDataFromDateCode(item.date, this.isSmallDevice)
					?.formatted,
			}));
		},
		answersTableHeaders() {
			return this.isSmallDevice
				? ["Date", "Wordle #", "Answer"]
				: ["Date", "Wordle Puzzle", "Wordle Answer"];
		},
	},
	methods: {
		loadData() {
			const params = {
				hints: true,
				past: true,
				date: this.getDateParam(this.isYesterdayPage),
				is_yesterday: this.isYesterdayPage || false,
			};

			return new Promise((resolve, reject) => {
				this.$axios
					.get(`${this.$config.API_SERVICE}/v4/wordle`, { params })
					.then((response) => {
						if (response.data.status === 200) {
							this.apiData = response.data.today;
							if (response.data.today.hints) {
								this.hints = response.data.today.hints;
							}
							if (response.data.past) {
								this.pastData = response.data.past;
							}

							return resolve();
						}
					})
					.catch((err) => reject(err));
			});
		},
	},
};
